/*
 * Reducer to fetch projects paginated information.
 */

import axios from 'axios';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { AsyncAction } from '..';
import { ProjectSummary } from '../../types/project';

export type Filter =
    | ''
    | 'upcoming'
    | 'past due'
    | 'not planned'
    | 'ready to inspect'
    | 'in progress'
    | 'awaiting report'
    | 'under review'
    | 'completed';

type State = {
    fetching: boolean;
    filter: Filter;
    interval: Interval;
    query?: string;
    sort?: { key: string; order?: 'ascending' | 'descending' };
    pageSize?: number;
    count: number;
    projects: ProjectSummary[];
    error?: Error;
};

type Action =
    | { type: 'project_list/reset' }
    | {
          type: 'project_list/list';
          start?: { filter: Filter };
          payload?: {
              filter: Filter;
              interval: Interval;
              query: string;
              sort?: { key: string; order?: 'ascending' | 'descending' };
              count: number;
              pageSize: number;
              pages: number;
              page: number;
              projects: ProjectSummary[];
          };
          error?: Error;
      };

const initialState: State = {
    fetching: false,
    filter: 'upcoming',
    interval: { start: new Date(), end: addDays(new Date(), 90) },
    count: 0,
    projects: [],
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'project_list/reset': {
            return { ...initialState };
        }
        case 'project_list/list': {
            if (action.payload)
                return {
                    ...state,
                    fetching: false,
                    filter: action.payload.filter,
                    interval: action.payload.interval,
                    count: action.payload.count,
                    query: action.payload.query,
                    sort: action.payload.sort,
                    pageSize: action.payload.pageSize,
                    projects: action.payload.projects,
                };

            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true, filter: action.start!.filter };
        }
        default:
            return state;
    }
};

const reset = (): Action => ({ type: 'project_list/reset' });

const list = (
    params: {
        filter?: Filter;
        interval?: Interval;
        page?: number;
        query?: string;
        sort?: { key: string; order?: 'ascending' | 'descending' };
        pageSize?: number;
        extraParams?: { [param: string]: string };
    },
    rethrow = false,
): AsyncAction => async (dispatch, getState) => {
    const { project_list } = getState();
    const filter = params.filter ?? project_list.filter;
    const interval = params.interval ?? project_list.interval;
    const query = params.query ?? project_list.query;
    let sort = params.sort ?? project_list.sort;
    sort = sort?.order != undefined ? sort : undefined;
    const pageSize = params.pageSize ?? project_list.pageSize;
    try {
        dispatch({ type: 'project_list/list', start: { filter } });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'project_list/list': cancel } });
        const filterParams =
            filter === ''
                ? {}
                : filter === 'upcoming'
                ? { upcoming: 'True' }
                : filter === 'past due'
                ? { past_due: 'True' }
                : filter === 'not planned'
                ? { inspection_plan__isnull: 'True' }
                : { status: filter };
        const paginationParams = {
            pagination: params.page == undefined ? 'False' : 'True',
            page: (params.page ?? 0) + 1,
        };
        const intervalParams =
            params.page == undefined || ['', 'upcoming', 'ready to inspect'].includes(filter)
                ? {
                      start_date:
                          params.page == undefined || filter !== 'ready to inspect'
                              ? startOfDay(interval.start)
                              : new Date(0),
                      end_date: endOfDay(interval.end),
                  }
                : {};
        const sortParams = sort ? { ordering: `${sort.order === 'ascending' ? '' : '-'}${sort.key}` } : {};
        const response = await axios.get<
            { count: number; next?: string; results: ProjectSummary[] } | ProjectSummary[]
        >('/inspections/overview/', {
            params: {
                ...filterParams,
                ...paginationParams,
                ...intervalParams,
                search: query,
                ...sortParams,
                page_size: pageSize,
                ...params.extraParams,
            },
            cancelToken: cancel.token,
        });
        const count = response.data instanceof Array ? response.data.length : response.data.count;
        const pages =
            response.data instanceof Array
                ? 1
                : response.data.next != undefined || params.page === 0
                ? Math.ceil(response.data.count / Math.max(response.data.results.length, 1))
                : project_list.projects.length;
        dispatch({
            type: 'project_list/list',
            payload: {
                filter,
                interval,
                count,
                pages,
                query: query ?? '',
                sort,
                page: params.page ?? 0,
                pageSize: pageSize ?? 10,
                projects: response.data instanceof Array ? [] : response.data.results,
            },
        });
    } catch (error) {
        if (error.name === 'AbortError') return;
        dispatch({ type: 'project_list/list', error });
        if (rethrow) throw error;
    }
};

export const creators = { reset, list };
